import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import WhiteLabelFields from './WhiteLabelFields';
import { OrgProvisionSuccessModal } from './OrgOperatorPanel';
import { emitToast } from '../../context/toast';
import PhoneField from '../ui/PhoneField';

const emptyForm = {
  nombre: '',
  brand_name: '',
  email: '',
  telefono: '',
  descripcion: '',
  experticia: '',
  especialidad: 'Entrenamiento personalizado',
  admin_password: '',
  admin_email: '',
  include_recommended_content: true,
  logo_url: '',
  brand_slug: '',
  welcome_message: '',
  support_whatsapp: '',
  primary_color: '#2563eb',
  secondary_color: '#10b981',
};

export default function CreateCoachOrgModal({ open, onClose, onCreated }) {
  const { t } = useI18n();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [provisionModal, setProvisionModal] = useState(null);
  const submitLock = useRef(false);
  const backdropMouseDown = useRef(false);

  const formHasContent = Boolean(
    form.nombre?.trim()
    || form.email?.trim()
    || form.brand_name?.trim()
    || form.telefono?.trim()
    || form.descripcion?.trim()
    || form.experticia?.trim()
    || form.admin_email?.trim()
    || form.admin_password?.trim(),
  );

  const requestClose = () => {
    if (saving) return;
    if (formHasContent && !window.confirm(t('orgs.discard_coach_form', '¿Cerrar sin guardar los datos del formulario?'))) {
      return;
    }
    onClose?.();
  };

  useEffect(() => {
    if (!open) return;
    setError('');
    setForm(emptyForm);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || saving) return;
      if (formHasContent && !window.confirm(t('orgs.discard_coach_form', '¿Cerrar sin guardar los datos del formulario?'))) {
        return;
      }
      onClose?.();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, saving, formHasContent, onClose, t]);

  if (!open) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };
      if (name === 'email' && (!prev.admin_email || prev.admin_email === prev.email)) {
        next.admin_email = value;
      }
      return next;
    });
  };

  const finishCreateSuccess = (body) => {
    const trainer = body.trainer || body;
    onCreated?.();
    onClose?.();
    if (body.admin_credentials || body.admin_user || body.invite_code || trainer?.invite_code) {
      setProvisionModal({
        title: t('orgs.coach_created', 'Organización coach creada'),
        inviteCode: body.invite_code || trainer?.invite_code,
        adminUser: body.admin_user,
        adminCredentials: body.admin_credentials,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitLock.current || saving) return;
    submitLock.current = true;
    setSaving(true);
    setError('');
    try {
      const contactEmail = form.email.trim().toLowerCase();
      const adminEmail = form.admin_email?.trim().toLowerCase() || contactEmail;
      const res = await api.post('/trainers', {
        nombre: form.nombre.trim(),
        email: contactEmail,
        telefono: form.telefono?.trim() || null,
        especialidad: form.especialidad?.trim() || null,
        certificaciones: form.experticia?.trim() ? [form.experticia.trim()] : [],
        descripcion: form.descripcion?.trim() || undefined,
        copy_gallery_from_nico: form.include_recommended_content === true,
        copy_routines_from_nico: form.include_recommended_content === true,
        admin_password: form.admin_password?.trim() || undefined,
        admin_email: adminEmail !== contactEmail ? adminEmail : undefined,
        white_label_enabled: true,
        brand_name: form.brand_name?.trim() || form.nombre.trim(),
        logo_url: form.logo_url?.trim() || undefined,
        brand_slug: form.brand_slug?.trim() || undefined,
        welcome_message: form.descripcion?.trim() || form.welcome_message?.trim() || undefined,
        support_whatsapp: form.support_whatsapp?.trim() || undefined,
        primary_color: form.primary_color || undefined,
        secondary_color: form.secondary_color || undefined,
        favicon_url: form.favicon_url?.trim() || undefined,
        cover_url: form.cover_url?.trim() || undefined,
        social_links: form.social_links || undefined,
        custom_domain: form.custom_domain?.trim() || undefined,
      });
      const body = res.data || {};
      finishCreateSuccess(body);
      if (body.admin_provision_skipped) {
        emitToast({
          type: 'warning',
          title: t('orgs.coach_admin_missing', 'Coach creado sin cuenta de login'),
          message: t('orgs.coach_admin_missing_hint', 'Abre Gestionar → Administrador y crea el usuario, o ejecuta npm run repair:coach-operators'),
        });
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.error;
      if (status === 409) {
        setError(msg || t('orgs.coach_duplicate_email', 'Ya existe una organización coach con este correo. Revisa la lista o usa otro email.'));
      } else {
        setError(msg || t('orgs.coach_create_error', 'No se pudo crear la organización coach'));
      }
    } finally {
      submitLock.current = false;
      setSaving(false);
    }
  };

  return (
    <>
      <div
        className="form-modal-overlay"
        style={{ zIndex: 9998 }}
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => {
          backdropMouseDown.current = e.target === e.currentTarget;
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget && backdropMouseDown.current) {
            requestClose();
          }
          backdropMouseDown.current = false;
        }}
      >
        <div className="form-modal d28d-admin-modal ui-light-panel surface-readable bg-white border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <div className="form-modal-header">
            <h3 className="font-bold">{t('orgs.create_coach', 'Nueva organización coach')}</h3>
            <button type="button" className="form-close-btn" onClick={requestClose} aria-label="Cerrar">
              <X size={18} />
            </button>
          </div>
          <form className="form-modal-content space-y-4" onSubmit={handleSubmit}>
            <p className="text-sm text-stone-600">
              {t('orgs.create_coach_hint', 'Crea una organización coach con su administrador, marca y licencias gestionables desde Organizaciones.')}
            </p>
            {error ? <div className="alert-error text-sm">{error}</div> : null}
            <label className="block text-sm">
              <span className="font-semibold">{t('orgs.field_coach_name', 'Nombre de la organización')}</span>
              <input className="input mt-1 w-full" name="nombre" required value={form.nombre} onChange={handleChange} />
            </label>
            <label className="block text-sm">
              <span className="font-semibold">{t('tenants.field_brand', 'Marca visible (opcional)')}</span>
              <input className="input mt-1 w-full" name="brand_name" value={form.brand_name} onChange={handleChange} placeholder={form.nombre || 'Mi coach'} />
            </label>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="font-semibold">{t('tenants.field_contact_email', 'Email de contacto')}</span>
                <input className="input mt-1 w-full" type="email" name="email" required value={form.email} onChange={handleChange} />
                <span className="text-xs text-stone-500 mt-1 block">
                  {t('orgs.coach_contact_email_hint', 'Debe ser único. No uses un correo que ya tenga cuenta de usuario final en la plataforma.')}
                </span>
              </label>
              <PhoneField
                label={t('common.phone', 'Teléfono')}
                value={form.telefono}
                onChange={(v) => setForm((prev) => ({ ...prev, telefono: v }))}
                inputClassName="input mt-1 w-full"
                className="block text-sm"
                labelClassName="font-semibold"
              />
            </div>
            <label className="block text-sm">
              <span className="font-semibold">{t('orgs.coach_admin_email', 'Email administrador (login, opcional)')}</span>
              <input className="input mt-1 w-full" type="email" name="admin_email" value={form.admin_email} onChange={handleChange} placeholder={form.email || 'admin.mi-coach@foodplan.local'} />
              <span className="text-xs text-stone-500 mt-1 block">
                {t('orgs.coach_admin_email_hint', 'Si el contacto ya está ocupado, indica aquí otro correo para el login del entrenador. Si lo dejas vacío, se intentará generar uno automático.')}
              </span>
            </label>
            <label className="block text-sm">
              <span className="font-semibold">{t('orgs.coach_description', 'Descripción')}</span>
              <textarea
                className="input mt-1 w-full min-h-[72px]"
                name="descripcion"
                value={form.descripcion}
                onChange={handleChange}
                placeholder={t('orgs.coach_description_hint', 'Presenta tu organización coach a los usuarios')}
              />
            </label>
            <label className="block text-sm">
              <span className="font-semibold">{t('orgs.coach_expertise', 'Experticia')}</span>
              <textarea
                className="input mt-1 w-full min-h-[64px]"
                name="experticia"
                value={form.experticia}
                onChange={handleChange}
                placeholder={t('orgs.coach_expertise_hint', 'Áreas en las que destaca el coach')}
              />
            </label>
            <label className="block text-sm">
              <span className="font-semibold">{t('orgs.coach_specialty', 'Especialidad')}</span>
              <input
                className="input mt-1 w-full"
                name="especialidad"
                value={form.especialidad}
                onChange={handleChange}
                placeholder={t('orgs.coach_specialty_hint', 'Ej: Hipertrofia, pérdida de grasa, movilidad')}
              />
            </label>
            <label className="block text-sm">
              <span className="font-semibold">{t('tenants.field_admin_password', 'Contraseña admin (opcional)')}</span>
              <input className="input mt-1 w-full" type="password" name="admin_password" value={form.admin_password} onChange={handleChange} autoComplete="new-password" placeholder={t('org.operator.auto_password', 'Vacío = generar automática')} />
            </label>
            <label className="flex items-start gap-2 text-sm text-stone-700 rounded-lg border border-slate-200 bg-stone-50 p-3">
              <input
                type="checkbox"
                name="include_recommended_content"
                checked={form.include_recommended_content === true}
                onChange={handleChange}
                className="mt-0.5"
              />
              <span>{t('orgs.include_recommended_content', 'Incluir contenido inicial recomendado')}</span>
            </label>
            <WhiteLabelFields formData={form} setFormData={setForm} />
            <div className="flex gap-2 pt-2">
              <button type="button" className="btn-secondary flex-1" onClick={requestClose} disabled={saving}>{t('common.cancel', 'Cancelar')}</button>
              <button type="submit" className="btn-primary flex-1" disabled={saving}>
                {saving ? t('common.saving', 'Guardando…') : t('orgs.create_coach_submit', 'Crear organización coach')}
              </button>
            </div>
          </form>
        </div>
      </div>
      <OrgProvisionSuccessModal
        open={!!provisionModal}
        onClose={() => setProvisionModal(null)}
        title={provisionModal?.title}
        inviteCode={provisionModal?.inviteCode}
        adminUser={provisionModal?.adminUser}
        adminCredentials={provisionModal?.adminCredentials}
        entityLabel="organización coach"
      />
    </>
  );
}
